// main.js
import { createApp, watch } from "vue";
import App from "./App.vue";
import "@fortawesome/fontawesome-free/css/all.min.css";
import router from "./router";
import store from "./store";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import "@/assets/css/theme-colors.css";
import "@/assets/css/global.css";
import "@/assets/css/fonts.css";
import VueCookies from "vue3-cookies";
import i18n from "./i18n";
import AclPlugin from "@/plugins/acl";
import { initEcho } from "../resources/js/echo.js";

const app = createApp(App);

const applyLangDir = (loc) => {
  const ar = loc === "ar";
  document.documentElement.setAttribute("dir", ar ? "rtl" : "ltr");
  document.documentElement.setAttribute("lang", ar ? "ar" : "en");
};

// Tell OneSignal which language to use for push content ("en"/"ar"),
// matching the language the person picked INSIDE the app rather than
// their browser/OS locale -- OneSignal otherwise auto-detects language
// from the browser, which is why notifications were showing in English
// for people who'd switched the app itself to Arabic. Safe to call
// before the SDK/subscription exists -- OneSignalDeferred just queues it.
const syncOneSignalLanguage = (loc) => {
  if (!window.OneSignalDeferred) return;
  window.OneSignalDeferred.push(async function (OneSignal) {
    try {
      await OneSignal.User.setLanguage(loc === "ar" ? "ar" : "en");
    } catch (e) {
      console.warn("OneSignal setLanguage failed:", e);
    }
  });
};

applyLangDir(i18n.global.locale.value);

watch(
  () => i18n.global.locale.value,
  (loc) => {
    localStorage.setItem("locale", loc);
    applyLangDir(loc);
    syncOneSignalLanguage(loc);
  }
);

app.use(i18n);
app.use(store);
app.use(router);
app.use(AclPlugin);
app.use(VueCookies);

app.use(Toast, {
  timeout: 2500,
  rtl: i18n.global.locale.value === "ar",
  position: "top-right",
});

app.mount("#app");

if (store.state.token && store.state.user) {
  initEcho();
  store.dispatch("loadMyPermissions").catch(() => {});

  // Mirror the OneSignal linking/prompt we do right after a fresh login
  // (see store/index.js's loginUser action) for the "already logged in,
  // just reopened/refreshed the tab" case -- otherwise someone who never
  // logs out again would never get asked for push permission, since this
  // branch (session restored from localStorage) runs instead of loginUser
  // on every page load after the first one.
  const restoredUser = store.state.user;
  const restoredTenant = store.state.tenant;
  if (window.OneSignalDeferred && restoredTenant?.slug && restoredUser?.role?.slug) {
    const externalId = `${restoredTenant.slug}:${restoredUser.role.slug}:${restoredUser.id}`;
    window.OneSignalDeferred.push(async function (OneSignal) {
      try {
        await OneSignal.login(externalId);
        await OneSignal.User.setLanguage(i18n.global.locale.value === "ar" ? "ar" : "en");
        await OneSignal.Slidedown.promptPush();
      } catch (e) {
        console.warn("OneSignal session-restore setup failed:", e);
      }
    });
  }
}
